import type { AppModuleKey, User, UserPermissionGrant } from "./types";

const moduleViewPermissionByModule: Partial<Record<AppModuleKey, string>> = {
  ADMIN_LOGS: "MODULE_ADMIN_LOGS_VIEW",
  ADMIN_SYSTEM_SETTINGS: "MODULE_ADMIN_SYSTEM_SETTINGS_VIEW",
  ADMIN_SYSTEM_ROUTINES: "SYSTEM_ROUTINES_VIEW",
  ADMIN_REPORTS: "MODULE_REPORTS_ADMIN_VIEW",
  ADMIN_THIRD_PARTIES: "MODULE_ADMIN_THIRD_PARTIES_VIEW",
  ADMIN_USERS: "USER_MANAGEMENT_VIEW",
  ASSET_MANAGEMENT: "MODULE_ASSETS_VIEW",
  AUTOMATIONS: "MODULE_AUTOMATIONS_VIEW",
  MATERIAL_REQUESTS: "MODULE_MATERIAL_REQUESTS_VIEW",
  DOCUMENTS: "MODULE_DOCUMENTS_VIEW",
  APR_MANAGEMENT: "MODULE_APR_VIEW",
  PLANNING: "MODULE_PLANNING_VIEW",
  POSTS: "MODULE_POSTS_VIEW",
  REPORTS: "MODULE_REPORTS_VIEW",
  TEAMS: "TEAM_VIEW",
  PROJECTS: "PROJECT_VIEW",
  TRAININGS: "MODULE_TRAININGS_VIEW",
};

export function canViewModule(user: User, module: AppModuleKey) {
  if (module === "ADMIN_USERS") {
    return canAccessUserManagement(user);
  }

  if (isSystemMaster(user.permissionGrants)) {
    return true;
  }

  const modulePermissionKey = moduleViewPermissionByModule[module];

  if (modulePermissionKey) {
    return hasPermission(user.permissionGrants, modulePermissionKey);
  }

  if (module === "HOME") {
    return true;
  }

  if (module === "COMMUNICATIONS") {
    return hasAnyPermission(user.permissionGrants, [
      "SYSTEM_ANNOUNCEMENT_MANAGE",
      "SYSTEM_MAINTENANCE_MANAGE",
    ]);
  }

  if (module === "ADMIN_THIRD_PARTIES" || module === "ADMIN_LOGS") {
    return false;
  }

  if (user.modulePermissions?.length) {
    return user.modulePermissions.some(
      (permission) => permission.module === module && permission.canView,
    );
  }

  return false;
}

export function hasAnyUserManagementPermission(user: User) {
  if (
    isSystemMaster(user.permissionGrants) ||
    hasAnyPermission(user.permissionGrants, [
      "USER_CREATE",
      "USER_EDIT",
      "USER_INACTIVATE",
      "USER_DELETE",
      "USER_PERMISSION_MANAGE",
    ])
  ) {
    return true;
  }

  return false;
}

export function canAccessUserManagement(user: User) {
  const editScope = resolvePermissionScope(user.permissionGrants, "USER_EDIT");

  if (editScope === "OWN") {
    return false;
  }

  return (
    isSystemMaster(user.permissionGrants) ||
    editScope === "GLOBAL" ||
    hasPermission(user.permissionGrants, "USER_MANAGEMENT_VIEW")
  );
}

export function resolvePermissionScope(
  permissions: UserPermissionGrant[] | undefined,
  permissionKey: string,
) {
  const grant = permissions?.find(
    (item) => item.permissionKey === permissionKey,
  );

  if (!grant?.value) {
    return "NONE" as const;
  }

  return grant.scope === "OWN" || grant.scope === "GLOBAL"
    ? grant.scope
    : ("NONE" as const);
}

export function hasPermission(
  permissions: UserPermissionGrant[] | undefined,
  permissionKey: string,
) {
  const grant = permissions?.find(
    (item) => item.permissionKey === permissionKey,
  );

  if (!grant) {
    return false;
  }

  if (grant.scope) {
    return grant.scope === "OWN" || grant.scope === "GLOBAL";
  }

  return grant.value;
}

export function hasBooleanPermission(
  permissions: UserPermissionGrant[] | undefined,
  permissionKey: string,
) {
  const grant = permissions?.find(
    (item) => item.permissionKey === permissionKey,
  );

  return Boolean(grant?.value && !grant.scope);
}

export function hasAnyPermission(
  permissions: UserPermissionGrant[] | undefined,
  permissionKeys: string[],
) {
  return permissionKeys.some((permissionKey) =>
    hasPermission(permissions, permissionKey),
  );
}

export function hasModuleAccess(
  permissions: UserPermissionGrant[] | undefined,
  modulePermissionKey: string,
) {
  return hasPermission(permissions, modulePermissionKey);
}

export function isSystemMaster(permissions: UserPermissionGrant[] | undefined) {
  return hasPermission(permissions, "SYSTEM_MASTER_BYPASS");
}
